import React from 'react';
import { ArrowRight } from 'lucide-react';
import SEOMeta, { PAGE_META } from '../../components/SEOMeta';
import { domains } from './cfqData';

const TCL_ICON_URL = 'https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/c4h0snj7_Third%20culture%20Leadership%20icon.png';

// Noise SVG overlay for matte texture
const NOISE_BG = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E\")";

const CFQAssessmentForm = ({
  userDetails, setUserDetails,
  responses, handleResponseChange,
  handleSubmit,
  answeredQuestions, totalQuestions, progressPercent,
}) => (
  <div className="min-h-screen" style={{ backgroundColor: '#0B1628', backgroundImage: NOISE_BG }}>
    <SEOMeta url="/cfq-assessment" {...PAGE_META.cfqAssessment} />

    {/* ── Hero ─────────────────────────────────────────────────────────── */}
    <div className="relative overflow-hidden" style={{ backgroundColor: '#071B3A' }}>
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: 'radial-gradient(circle at 70% 50%, #C99316 0%, transparent 60%)' }}
      />
      <div className="relative z-10 py-20 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex justify-center mb-8">
            <div
              className="flex items-center justify-center"
              style={{ width: '68px', height: '68px', borderRadius: '14px', backgroundColor: 'rgba(245,240,232,0.06)', border: '1px solid rgba(245,240,232,0.10)' }}
            >
              <img src={TCL_ICON_URL} alt="Third Culture Leadership" style={{ width: '52px', height: '52px', objectFit: 'contain' }} />
            </div>
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-8" style={{ background: 'rgba(201,147,22,0.08)', border: '1px solid rgba(201,147,22,0.2)' }}>
            <div className="w-1.5 h-1.5 rounded-full bg-[#C99316] opacity-80" />
            <span className="text-xs uppercase tracking-[0.2em] font-medium" style={{ color: '#C99316' }}>Leadership Diagnostic</span>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl font-light text-[#F5F0E8] mb-4 tracking-tight">
            Cultural Fluency Quotient
          </h1>
          <p className="text-base mb-3 font-medium tracking-wide" style={{ color: 'rgba(245,240,232,0.65)' }}>CFQ by Promise Monday</p>
          <div className="flex items-center justify-center gap-4 my-8">
            <div className="h-px w-16" style={{ background: 'rgba(201,147,22,0.25)' }} />
            <div className="w-2 h-2 rotate-45" style={{ border: '1px solid rgba(201,147,22,0.4)' }} />
            <div className="h-px w-16" style={{ background: 'rgba(201,147,22,0.25)' }} />
          </div>
          <div className="rounded-xl p-6 max-w-xl mx-auto mb-6" style={{ background: 'rgba(245,240,232,0.04)', border: '1px solid rgba(245,240,232,0.08)' }}>
            <p className="font-serif text-xl italic mb-2" style={{ color: '#C99316' }}>This is not a test. It&apos;s a mirror.</p>
            <p className="text-[17px] leading-relaxed" style={{ color: 'rgba(245,240,232,0.80)' }}>
              Assess your ability to lead, communicate, and integrate across cultures with presence and integrity.
            </p>
          </div>
          <p className="text-[13px]" style={{ color: 'rgba(245,240,232,0.50)' }}>Your responses are used only to generate your results. Nothing is stored or shared.</p>
        </div>
      </div>
    </div>

    {/* ── Scale Legend ─────────────────────────────────────────────────── */}
    <div className="py-6 px-6" style={{ backgroundColor: '#071B3A', borderBottom: '1px solid rgba(245,240,232,0.05)' }}>
      <div className="max-w-3xl mx-auto">
        <div className="rounded-xl p-5" style={{ background: 'rgba(245,240,232,0.03)', border: '1px solid rgba(245,240,232,0.07)' }}>
          <p className="text-[10px] uppercase tracking-[0.2em] text-center mb-4 font-medium" style={{ color: 'rgba(245,240,232,0.35)' }}>Response Scale</p>
          <div className="flex items-center justify-between max-w-sm mx-auto">
            {[
              { value: 1, label: 'Rarely' },
              { value: 2, label: 'Sometimes' },
              { value: 3, label: 'Often' },
              { value: 4, label: 'Usually' },
              { value: 5, label: 'Consistently' },
            ].map((item) => (
              <div key={item.value} className="flex flex-col items-center gap-1.5">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-semibold"
                  style={{ background: 'rgba(201,147,22,0.10)', border: '1px solid rgba(201,147,22,0.22)', color: '#C99316' }}
                >
                  {item.value}
                </div>
                <span className="text-[12px] uppercase tracking-wider sm:block" style={{ color: 'rgba(245,240,232,0.40)' }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* ── Sticky Progress ──────────────────────────────────────────────── */}
    <div
      className="sticky z-40 py-3 px-5 sm:px-6"
      style={{ top: '70px', backgroundColor: 'rgba(11,22,40,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(201,147,22,0.12)' }}
      data-testid="cfq-progress-bar"
    >
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm" style={{ color: 'rgba(245,240,232,0.40)' }}>
            Step {Math.min(Math.ceil((answeredQuestions / 5) + 0.01), 4)} of 4 — {domains[Math.min(Math.floor(answeredQuestions / 5), 3)]?.name || 'Complete'}
          </span>
          <span className="text-sm font-medium" style={{ color: '#C99316' }}>{answeredQuestions} / {totalQuestions}</span>
        </div>
        <div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(245,240,232,0.07)' }}>
          <div className="h-full rounded-full transition-all duration-500 ease-out" style={{ width: `${progressPercent}%`, background: '#C99316' }} />
        </div>
        <div className="flex justify-between mt-2">
          {domains.map((d, i) => {
            const done = Object.keys(responses).filter(k => parseInt(k.split('-')[0]) === i).length;
            const complete = done >= 5;
            const active = answeredQuestions >= i * 5 && answeredQuestions < (i + 1) * 5;
            return (
              <div key={d.name} className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full transition-colors" style={{ background: complete ? '#C99316' : active ? 'rgba(201,147,22,0.4)' : 'rgba(245,240,232,0.12)' }} />
                <span className="text-[10px] hidden sm:inline" style={{ color: complete ? '#C99316' : active ? 'rgba(245,240,232,0.6)' : 'rgba(245,240,232,0.25)' }}>{d.name.split('-')[0]}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>

    {/* ── Your Details ─────────────────────────────────────────────────── */}
    <div className="py-10 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="rounded-xl p-6 sm:p-8" style={{ background: '#12223A', border: '1px solid rgba(245,240,232,0.07)' }}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(201,147,22,0.08)', border: '1px solid rgba(201,147,22,0.18)' }}>
              <span style={{ color: '#C99316', fontSize: '14px' }}>✦</span>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] font-medium" style={{ color: 'rgba(245,240,232,0.35)' }}>Your Details</p>
              <p className="text-[10px] mt-0.5" style={{ color: 'rgba(245,240,232,0.2)' }}>Optional</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { key: 'name', placeholder: 'Name', type: 'text' },
              { key: 'role', placeholder: 'Role', type: 'text' },
              { key: 'organisation', placeholder: 'Organisation', type: 'text' },
              { key: 'date', placeholder: 'Date', type: 'date' },
            ].map((field) => (
              <input
                key={field.key}
                type={field.type}
                value={userDetails[field.key]}
                onChange={(e) => setUserDetails({ ...userDetails, [field.key]: e.target.value })}
                className="w-full rounded-lg px-4 py-3.5 text-[16px] transition-all focus:outline-none"
                style={{
                  background: 'rgba(245,240,232,0.04)',
                  border: '1px solid rgba(245,240,232,0.10)',
                  color: '#F5F0E8',
                  colorScheme: 'dark',
                }}
                onFocus={e => { e.target.style.borderColor = 'rgba(201,147,22,0.4)'; e.target.style.boxShadow = '0 0 0 3px rgba(201,147,22,0.08)'; }}
                onBlur={e => { e.target.style.borderColor = 'rgba(245,240,232,0.10)'; e.target.style.boxShadow = 'none'; }}
                placeholder={field.placeholder}
              />
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* ── Question Domains ─────────────────────────────────────────────── */}
    <form onSubmit={handleSubmit} className="pb-12">
      {domains.map((domain, domainIndex) => (
        <div key={domain.name} className="mb-6" data-testid={`cfq-domain-section-${domainIndex + 1}`}>

          {/* Domain header */}
          <div
            className="relative overflow-hidden py-9 px-6"
            style={{ backgroundColor: '#071B3A', borderTop: '1px solid rgba(245,240,232,0.04)', borderBottom: '1px solid rgba(201,147,22,0.08)' }}
          >
            <div
              className="absolute inset-0 opacity-[0.03]"
              style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #C99316 0%, transparent 60%)' }}
            />
            <div className="relative z-10 max-w-3xl mx-auto">
              <div className="flex items-center gap-5">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 text-lg font-semibold"
                  style={{ background: 'rgba(201,147,22,0.12)', border: '1px solid rgba(201,147,22,0.25)', color: '#C99316' }}
                >
                  0{domainIndex + 1}
                </div>
                <div>
                  <h2 className="font-serif text-[28px] md:text-[32px] text-[#F5F0E8] font-light tracking-tight">{domain.name}</h2>
                  <p className="text-[16px] mt-1.5 font-medium" style={{ color: 'rgba(201,147,22,0.75)' }}>{domain.subtitle}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Questions */}
          <div className="px-6 pt-4">
            <div className="max-w-3xl mx-auto rounded-xl overflow-hidden" style={{ border: '1px solid rgba(245,240,232,0.07)' }}>
              {domain.statements.map((statement, stmtIdx) => {
                const key = `${domainIndex}-${stmtIdx}`;
                const selected = responses[key];
                return (
                  <div
                    key={key}
                    className="transition-colors"
                    style={{
                      background: selected ? 'rgba(201,147,22,0.03)' : '#12223A',
                      borderBottom: stmtIdx !== domain.statements.length - 1 ? '1px solid rgba(245,240,232,0.05)' : 'none',
                    }}
                    data-testid={`cfq-question-${domainIndex * 5 + stmtIdx + 1}`}
                  >
                    <div className="p-6 md:p-7">
                      <div className="flex items-start gap-5">
                        {/* Question number */}
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-semibold transition-colors"
                          style={{
                            background: selected ? 'rgba(201,147,22,0.15)' : 'rgba(245,240,232,0.05)',
                            color: selected ? '#C99316' : 'rgba(245,240,232,0.25)',
                            border: selected ? '1px solid rgba(201,147,22,0.25)' : '1px solid rgba(245,240,232,0.08)',
                          }}
                        >
                          {stmtIdx + 1}
                        </div>
                        <div className="flex-1">
                          {/* Statement — larger text */}
                          <p
                            className="text-[18px] sm:text-[20px] leading-[1.6] mb-5"
                            style={{ color: 'rgba(245,240,232,0.88)' }}
                            data-testid={`cfq-question-statement-${domainIndex * 5 + stmtIdx + 1}`}
                          >
                            {statement}
                          </p>
                          {/* Scale buttons */}
                          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                            {[1, 2, 3, 4, 5].map((value) => {
                              const isSelected = selected === value;
                              return (
                                <button
                                  key={value}
                                  type="button"
                                  onClick={() => handleResponseChange(domainIndex, stmtIdx, value)}
                                  className="w-[52px] h-[52px] sm:w-[60px] sm:h-[60px] rounded-xl text-[17px] font-semibold transition-all duration-200 touch-manipulation active:scale-95 flex-shrink-0"
                                  style={{
                                    background: isSelected ? '#C99316' : 'rgba(245,240,232,0.05)',
                                    border: isSelected ? '1px solid #C99316' : '1px solid rgba(245,240,232,0.12)',
                                    color: isSelected ? '#0B1628' : 'rgba(245,240,232,0.50)',
                                    transform: isSelected ? 'scale(1.08)' : 'scale(1)',
                                    boxShadow: isSelected ? '0 4px 16px rgba(201,147,22,0.25)' : 'none',
                                  }}
                                  data-testid={`cfq-option-${domainIndex}-${stmtIdx}-${value}`}
                                >
                                  {value}
                                </button>
                              );
                            })}
                            {/* Mobile label hint */}
                            {selected && (
                              <span className="text-[13px] ml-1.5" style={{ color: 'rgba(245,240,232,0.58)' }}>
                                {{1:'Rarely',2:'Sometimes',3:'Often',4:'Usually',5:'Consistently'}[selected]}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}

      {/* ── Submit ───────────────────────────────────────────────────────── */}
      <div className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div
            className="rounded-2xl p-10 md:p-14"
            style={{ background: '#12223A', border: '1px solid rgba(201,147,22,0.15)' }}
          >
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
              style={{ background: 'rgba(201,147,22,0.12)', border: '1px solid rgba(201,147,22,0.22)' }}
            >
              <ArrowRight size={24} style={{ color: '#C99316' }} />
            </div>
            <h3 className="font-serif text-[28px] md:text-[32px] text-[#F5F0E8] font-light mb-3">Ready to See Your Results?</h3>
            <p className="text-[17px] mb-8 max-w-md mx-auto" style={{ color: 'rgba(245,240,232,0.72)' }}>
              Your responses will be analysed to generate your personal Cultural Fluency Profile.
            </p>
            <button
              type="submit"
              data-testid="submit-assessment-btn"
              className="group inline-flex items-center gap-3 px-10 py-4 rounded-xl text-[17px] font-semibold tracking-wide transition-all duration-200 hover:opacity-90 active:scale-95"
              style={{ background: '#C99316', color: '#0B1628' }}
            >
              View My Results <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="text-[13px] mt-8" style={{ color: 'rgba(245,240,232,0.42)' }}>
              Your responses are not stored. Results are generated locally.
            </p>
          </div>
        </div>
      </div>
    </form>
  </div>
);

export default CFQAssessmentForm;
